/**
 * Kennzahlkachel — die Reihe oben auf der Übersicht.
 *
 * Aufbau und Maße aus `components/data/MetricCard.jsx`: Der Symbolchip steht
 * **allein** in der ersten Zeile, die Veränderung rechts von ihm; Beschriftung,
 * Wert und Unterzeile folgen darunter als Block. Icon und Beschriftung
 * nebeneinander zu setzen sieht aufgeräumter aus und lässt die Kachel darunter
 * leer wirken — der Wert verliert dann seinen Platz als Hauptsache.
 *
 * **Eine bewusste fachliche Abweichung.** Dort färbt eine einzige
 * `channel`-Angabe Symbol *und* Zahl, und zwar mit der Spartenfarbe. Das
 * vermischt die Achsen (ADR 0013). Hier sind es zwei getrennte Angaben:
 *
 * - `commodity` färbt **nur das Symbol** — Wegweisung, keine Datenaussage.
 * - `quantity` färbt **nur die Zahl** — die Größenfarbe, die der Nutzer aus
 *   jedem Diagramm wiedererkennt.
 *
 * Eine Kachel, die keine Energiegröße zeigt (etwa „3 Geräte"), lässt `quantity`
 * weg und bekommt die normale Textfarbe. Eine Zahl in Ocker, die keine Erzeugung
 * ist, wäre schlimmer als eine graue.
 */
import type { ReactNode } from "react";
import { commodityColorVar, fontWeight, quantityColorVar } from "../tokens.js";

/**
 * Wohin die Veränderung zeigt und wie sie zu lesen ist.
 *
 * Die Bewertung ist **getrennt** von der Richtung, weil die Kachel sie nicht
 * kennt: Mehr Verbrauch ist schlechter, mehr Erzeugung ist besser, und mehr
 * Geräte ist gar nichts. Das Kit entscheidet das an der Sparte (`goodUp` gilt
 * dort für Solar) — das geht nur gut, solange jede Kachel genau einer Sparte
 * entspricht. Ohne `deltaTone` bleibt die Veränderung deshalb grau: Der Pfeil
 * sagt, wohin es ging, und mehr behauptet die Kachel nicht.
 */
export type DeltaDirection = "up" | "down";
export type DeltaTone = "neutral" | "good" | "bad";

export interface MetricCardProps {
  /** Die Beschriftung — „Verbrauch heute". */
  readonly label: ReactNode;
  /**
   * Der fertig formatierte Wert, aus `formatValue(...).text`. `null` heißt: Es
   * gibt ihn nicht. Dann steht ein Gedankenstrich da und keine Null — eine Null
   * wäre eine Messung, die niemand vorgenommen hat.
   */
  readonly value: string | null;
  /**
   * Die Einheit. Sie steht **auch dann**, wenn der Wert fehlt: „– kWh" sagt,
   * was hier erscheinen wird; ein nackter Strich sagt nur, dass etwas fehlt.
   */
  readonly unit?: string;
  /** Die kleine Zeile unter dem Wert — „gegenüber gestern". */
  readonly caption?: ReactNode;
  /** Das Symbol im Chip. Trägt die Spartenfarbe, wenn `commodity` gesetzt ist. */
  readonly icon?: ReactNode;
  /** Sparte für die Symbolfarbe: `electricity`, `water`, `heat`, `gas`. */
  readonly commodity?: string;
  /** Messgröße für die Zahlenfarbe: `grid_import`, `generation`, … */
  readonly quantity?: string;
  /**
   * Oben rechts, wo sonst die Veränderung steht — im Entwurf der Momentanwert
   * derselben Größe: „jetzt 2,34 kW" (HM-076). Die Zahl darunter ist eine
   * Summe über den Zeitraum, Leistung lässt sich nicht summieren; der
   * Momentanwert braucht deshalb einen eigenen Platz, und eine eigene Karte
   * dafür kostete eine Bildschirmhöhe für vier Zahlen. Steht zurück, wenn eine
   * Veränderung gesetzt ist.
   */
  readonly aside?: ReactNode;
  /** Die Veränderung als fertiger Text — „+8 %". */
  readonly delta?: string;
  readonly deltaDirection?: DeltaDirection;
  readonly deltaTone?: DeltaTone;
}

const DELTA_COLORS: Record<DeltaTone, string> = {
  neutral: "var(--text-muted)",
  good: "var(--hm-success)",
  bad: "var(--hm-danger)",
};

export function MetricCard({
  label,
  value,
  unit,
  caption,
  icon,
  aside,
  commodity,
  quantity,
  delta,
  deltaDirection,
  deltaTone = "neutral",
}: MetricCardProps) {
  return (
    <section
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 9,
        padding: 14,
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm)",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minWidth: 0 }}
      >
        {icon !== undefined && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              /* 28 Pixel, wie im Entwurf (HM-140) — vorher 32. */
              width: 28,
              height: 28,
              flexShrink: 0,
              borderRadius: "var(--radius-sm)",
              /**
               * Die weiche Hinterlegung entsteht aus der Spartenfarbe selbst,
               * statt aus einer zweiten Palette von Pastelltönen: Eine neue
               * Sparte bringt ihre Hinterlegung damit von allein mit.
               */
              background:
                commodity === undefined
                  ? "var(--surface-sunken)"
                  : `color-mix(in srgb, ${commodityColorVar(commodity)} 16%, transparent)`,
              color: commodity === undefined ? "var(--text-muted)" : commodityColorVar(commodity),
            }}
          >
            {icon}
          </span>
        )}
        {aside !== undefined && delta === undefined && (
          <span
            style={{
              /* 14 Pixel wie im Entwurf (HM-140); in einer schmalen Kachel wird
                 gekürzt statt über den Rand zu laufen. */
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontSize: 14,
              fontWeight: fontWeight.semibold,
              color: "var(--text-muted)",
              whiteSpace: "nowrap",
            }}
          >
            {aside}
          </span>
        )}
        {delta !== undefined && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              fontSize: 13,
              fontWeight: fontWeight.semibold,
              color: DELTA_COLORS[deltaTone],
            }}
          >
            {/* Kit: ein ausgefülltes Dreieck, gedreht. Gezeichnet und nicht
                getippt — „▲" fehlt in mancher Schrift und fällt dann auf eine
                fremde zurück. */}
            {deltaDirection !== undefined && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                aria-hidden="true"
                style={{ transform: deltaDirection === "up" ? "none" : "rotate(180deg)" }}
              >
                <path d="M6 2.5 9.5 7h-7L6 2.5Z" fill="currentColor" />
              </svg>
            )}
            {delta}
          </span>
        )}
      </div>

      <div>
        <div
          style={{
            marginBottom: 3,
            fontSize: 12.5,
            fontWeight: fontWeight.medium,
            color: "var(--text-muted)",
          }}
        >
          {label}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontSize: 25,
              fontWeight: fontWeight.semibold,
              letterSpacing: "var(--tracking-tight)",
              color:
                value === null
                  ? "var(--text-faint)"
                  : quantity === undefined
                    ? "var(--text-strong)"
                    : quantityColorVar(quantity),
            }}
          >
            {value ?? "–"}
          </span>
          {unit !== undefined && (
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 14,
                fontWeight: fontWeight.medium,
                color: "var(--text-muted)",
              }}
            >
              {unit}
            </span>
          )}
        </div>
        {caption !== undefined && (
          <div style={{ marginTop: 5, fontSize: 11.5, color: "var(--text-faint)" }}>{caption}</div>
        )}
      </div>
    </section>
  );
}
